/**
 * Public dispute-status guide.
 * Route: `/dispute-status` — permanent link the admin sends via SMS/email.
 *
 * Purpose: walk the client through how to check the status of their own
 * disputes on Experian, TransUnion, and Equifax, and then push them into
 * the client-update portal to report back with photos.
 *
 * Everything on one page, mobile-first, bright + vibrant so it works well
 * as an iMessage link preview and a phone-browser experience.
 */
import { useState } from "react";
import {
  Shield, AlertTriangle, ChevronRight, Camera, ArrowRight, ExternalLink,
  ClipboardCheck, Info, Lock, CheckCircle2, RefreshCw,
} from "lucide-react";

// Bureaus are ordered exactly the way the admin wrote them out.
const BUREAUS = [
  {
    id: "experian",
    name: "Experian",
    accent: "from-indigo-600 via-blue-600 to-cyan-500",
    ring: "ring-blue-400",
    chip: "bg-blue-500",
    bg: "bg-gradient-to-br from-blue-500/10 to-cyan-500/5",
    border: "border-blue-500/40",
    text: "text-blue-100",
    url: "https://www.freecreditreport.com",
    steps: [
      "Log in to your Experian account.",
      "Scroll all the way to the bottom of the page.",
      'Tap "Support".',
      'Tap "Help Center".',
      'Tap "File a Dispute".',
      'Tap "Start a New Dispute".',
      "You'll now see your negative accounts:",
    ],
    outcomes: [
      { good: false, label: 'If it says "Under Dispute"', detail: "Great — the investigation is still processing. Nothing else to do right now." },
      { good: false, label: "If the negative account is still there", detail: "You need to re-dispute it — do a second-round dispute per the steps I already sent you." },
      { good: true,  label: "If no negative accounts appear", detail: "🎉 The account has been REMOVED. Update me right away." },
    ],
  },
  {
    id: "transunion",
    name: "TransUnion",
    accent: "from-red-600 via-rose-600 to-pink-500",
    ring: "ring-red-400",
    chip: "bg-rose-500",
    bg: "bg-gradient-to-br from-rose-500/10 to-red-500/5",
    border: "border-rose-500/40",
    text: "text-rose-100",
    url: "https://ciam.tui.transunion.com/realms/TransUnion/protocol/openid-connect/auth?response_type=code&redirect_uri=https%3A%2F%2Fservice.transunion.com%2Fdss%2Flanding.page&state=xiBK8RijITyaaeng4rjGvnzJIqsm2nzU35bF_2g2eK8&client_id=Phoenix&scope=openid&redirectedFromSite=DSS",
    steps: [
      "Log in to your TransUnion account.",
      'Tap "Disputes".',
      "Scroll down.",
      "It will tell you the status of every dispute — Pending or Completed.",
    ],
    outcomes: [
      { good: false, label: '"Pending" or "In Progress"', detail: "The investigation is still running — keep waiting, no action needed." },
      { good: true,  label: '"Completed"',                detail: "Open it, screenshot the result, and send it to me via the client portal below." },
    ],
  },
  {
    id: "equifax",
    name: "Equifax",
    accent: "from-amber-500 via-orange-500 to-red-500",
    ring: "ring-orange-400",
    chip: "bg-orange-500",
    bg: "bg-gradient-to-br from-orange-500/10 to-red-500/5",
    border: "border-orange-500/40",
    text: "text-orange-100",
    url: "https://my.equifax.com/membercenter/#/login",
    steps: [
      "Log in to Equifax.",
      'Tap "Dispute".',
      'Go to the "Dispute Center".',
      "The menu shows every open and closed dispute.",
    ],
    outcomes: [
      { good: true,  label: '"Closed" disputes', detail: "You can download the results PDF right there — send it to me through the client portal." },
      { good: false, label: '"Pending" disputes', detail: "When you go to those accounts they'll be highlighted / lit up — the investigation is still running." },
    ],
  },
];

export default function DisputeStatusGuidePage() {
  const [openBureau, setOpenBureau] = useState("experian");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-fuchsia-950 text-white overflow-x-hidden">
      {/* ─── Hero ────────────────────────────────────────────── */}
      <div className="relative">
        <div className="absolute inset-0 opacity-30 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle at 20% 20%, #ec4899, transparent 40%), radial-gradient(circle at 80% 30%, #06b6d4, transparent 45%), radial-gradient(circle at 60% 90%, #f59e0b, transparent 40%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-5 pt-12 pb-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 text-xs font-bold uppercase tracking-widest mb-4">
            <Shield className="w-3.5 h-3.5" />
            Credit Repair · Dispute Status Check
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight bg-gradient-to-br from-cyan-300 via-fuchsia-300 to-amber-200 text-transparent bg-clip-text">
            How to Check the Status
            <br />
            of Your Dispute
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-5 max-w-2xl mx-auto leading-relaxed">
            Follow these bureau-by-bureau steps to see exactly what&apos;s happening on your credit report — <span className="font-bold text-white">then come back and update me</span> so I know where we stand.
          </p>
        </div>
      </div>

      {/* ─── Critical notice ─────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-5 mt-2">
        <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/20 via-yellow-500/10 to-amber-500/5 p-5 sm:p-6 shadow-lg shadow-amber-500/10"
          data-testid="critical-notice">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/40">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-black text-amber-100 leading-tight">
                I have <span className="underline decoration-amber-300 decoration-4">NO WAY</span> to see your credit report
              </h2>
              <p className="text-sm sm:text-base text-amber-100/90 mt-2 leading-relaxed">
                Every bureau sends a <b>2-factor code</b> to <b>your phone or email</b> when I try to log in — I never see it. That means I <b>can&apos;t check your dispute status for you.</b> You&apos;ll need to check it yourself using the steps below and <b>update me through the client portal</b> (link at the bottom of this page).
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/25 border border-amber-400/40">
                  <Lock className="w-4 h-4 text-amber-200" />
                  <span className="text-xs font-bold text-amber-100">2-Factor blocks me</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/25 border border-amber-400/40">
                  <Camera className="w-4 h-4 text-amber-200" />
                  <span className="text-xs font-bold text-amber-100">Update me with pictures</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Bureau selector chips ──────────────────────────── */}
      <div className="max-w-3xl mx-auto px-5 mt-8">
        <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-3">
          Pick your bureau
        </h3>
        <div className="grid grid-cols-3 gap-2">
          {BUREAUS.map((b) => {
            const isActive = openBureau === b.id;
            return (
              <button key={b.id}
                onClick={() => setOpenBureau(b.id)}
                className={`px-3 py-3 rounded-xl border-2 text-sm font-black uppercase tracking-wide transition ${
                  isActive
                    ? `bg-gradient-to-br ${b.accent} text-white border-white/50 shadow-lg`
                    : "bg-zinc-900/60 border-zinc-700 text-zinc-300 hover:border-zinc-500"
                }`}
                data-testid={`bureau-chip-${b.id}`}>
                {b.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Active bureau card ─────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-5 mt-4 mb-8">
        {BUREAUS.filter((b) => b.id === openBureau).map((b) => (
          <div key={b.id}
            className={`rounded-2xl border-2 ${b.border} ${b.bg} p-5 sm:p-6 shadow-xl transition`}
            data-testid={`bureau-card-${b.id}`}>
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${b.accent} flex items-center justify-center shadow-lg`}>
                  <ClipboardCheck className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white">{b.name}</h3>
              </div>
              <a href={b.url} target="_blank" rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-gradient-to-br ${b.accent} text-white text-xs font-bold hover:opacity-90 shadow-lg`}
                data-testid={`bureau-open-${b.id}`}>
                Open <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* Steps */}
            <div className="rounded-xl bg-black/40 border border-white/10 p-4 mb-4">
              <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-3">
                Step-by-step
              </div>
              <ol className="space-y-2.5">
                {b.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className={`shrink-0 w-6 h-6 rounded-full ${b.chip} text-white text-xs font-black flex items-center justify-center shadow-md`}>
                      {i + 1}
                    </div>
                    <div className="text-sm sm:text-base text-white leading-relaxed pt-0.5">
                      {step}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* What each outcome means */}
            <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-2">
              What each result means
            </div>
            <div className="space-y-2">
              {b.outcomes.map((o, i) => (
                <div key={i}
                  className={`rounded-lg border p-3 flex items-start gap-3 ${
                    o.good
                      ? "border-emerald-400/40 bg-emerald-500/10"
                      : "border-white/10 bg-white/5"
                  }`}>
                  <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                    o.good ? "bg-emerald-500" : "bg-zinc-700"
                  }`}>
                    {o.good
                      ? <CheckCircle2 className="w-4 h-4 text-white" />
                      : <RefreshCw className="w-4 h-4 text-white" />}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-black ${o.good ? "text-emerald-200" : "text-white"}`}>
                      {o.label}
                    </div>
                    <div className="text-xs text-zinc-300 mt-1 leading-relaxed">
                      {o.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ─── Photo tip ───────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-5 mb-6">
        <div className="rounded-2xl border-2 border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-500/15 to-purple-500/5 p-5 flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center shadow-lg">
            <Camera className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-black text-fuchsia-100">Send me screenshots</h3>
            <p className="text-sm text-fuchsia-100/90 mt-1 leading-relaxed">
              When you find your status, <b>screenshot the page</b> and upload it in the client portal below. Pictures are the fastest way for me to keep your file up to date.
            </p>
          </div>
        </div>
      </div>

      {/* ─── Final CTA — jump into the client portal ────────── */}
      <div className="max-w-3xl mx-auto px-5 pb-12">
        <a href="/client-update"
          className="group block rounded-2xl border-2 border-emerald-400/60 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-6 shadow-2xl shadow-emerald-500/30 transition hover:brightness-110 hover:-translate-y-0.5"
          data-testid="final-cta-client-portal">
          <div className="flex items-center gap-4">
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <ArrowRight className="w-8 h-8 text-white group-hover:translate-x-1 transition" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">
                Once you finish checking →
              </div>
              <div className="text-2xl sm:text-3xl font-black text-white leading-tight mt-1">
                Update Me Through the Client Portal
              </div>
              <div className="text-sm text-white/90 mt-1">
                Fastest way to keep me in the loop · Attach your screenshots here
              </div>
            </div>
          </div>
        </a>

        {/* Alt contact hint */}
        <p className="text-center text-xs text-zinc-500 mt-4 flex items-center justify-center gap-1.5">
          <Info className="w-3 h-3" />
          Prefer text? You can also reply directly — but the portal is best (photos, timestamps, everything in one place).
        </p>

        {/* Footer */}
        <p className="text-center text-[10px] text-zinc-600 mt-8 pb-4">
          <ChevronRight className="w-3 h-3 inline" /> Bookmark this page — you&apos;ll use it again next round.
        </p>
      </div>
    </div>
  );
}
